import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { ADMINISTRATOR_ROLE, ORGANIZER_ROLE } from "@model";

@Injectable({ providedIn: 'root' })
export class PermissionService {
  static _currentPermissions = new BehaviorSubject<FeaturePermission[] | undefined>(undefined);

  get currentPermissions() {
    return PermissionService._currentPermissions;
  }

  initPermissions(role?: string): void {
    switch (role) {
      case ORGANIZER_ROLE:
        this._initOrganiserPermission();
        break;
      case ADMINISTRATOR_ROLE:
        this._initAdminPermissions();
        break;
      default:
        this._initEmptyPermissions();
    }
  }

  private _initAdminPermissions() {
    this._setPermissions([
      Features.OrganisersList, Features.CategoriesTab, Features.DiscountsTab, Features.OrganisersEdit,
      Features.Administrators, Features.AccountStatements, Features.AccountStatementsDetails,
      Features.LeaguesList, Features.LeagueDetails, Features.RequestsList, Features.Users
    ]);
  }

  private _initOrganiserPermission() {
    this._setPermissions([
      Features.OrganiserProfile, Features.LeaguesList, Features.LeagueDetails,
      Features.Categories, Features.RequestsList, Features.DiscountsList
    ]);
  }

  private _initEmptyPermissions() {
    this.currentPermissions.next(undefined);
  }

  emptyPermissions() {
    this.currentPermissions.next(undefined);
  }

  checkPermission(feature: Features, permission: Permission): boolean {
    const featurePermission = this.currentPermissions.getValue()?.find(f => f.feature === feature);
    if (featurePermission) {
      return permission === Permission.View ? featurePermission.permission !== Permission.None : featurePermission.permission === Permission.Edit;
    }
    return false;
  }

  private _setPermissions(features: Features[]) {
    const featurePermissions = features.map(feature => new FeaturePermission(feature, Permission.Edit));
    this.currentPermissions.next(featurePermissions);
  }
}

export enum Features {
  OrganisersList = 'OrganisersList',
  MeasurementsList = 'MeasurementsList',
  OrganiserProfile = 'OrganiserProfile',
  DiscountsList = 'DiscountsList',
  Categories = 'Categories',
  CategoriesTab = 'CategoriesTab',
  DiscountsTab = 'DiscountsTab',
  OrganisersEdit = 'OrganisersEdit',
  Administrators = 'Administrators',
  AccountStatements = 'AccountStatements',
  AccountStatementsDetails = 'AccountStatementsDetails',
  LeaguesList = 'LeaguesList',
  LeagueDetails = 'LeagueDetails',
  RequestsList = 'RequestsList',
  Users = 'Users',
}

export enum Permission {
  None = 'None',
  View = 'View',
  Edit = 'Edit',
}

export class FeaturePermission {
  constructor(public feature: Features, public permission: Permission) {}
}
